import { BaseModel, type RelationConfig } from './base';

// Enums

export enum CampaignType {
  email = 0,
  sms = 1,
}

export const CampaignTypeLabels: Record<CampaignType, string> = {
  [CampaignType.email]: 'general.email',
  [CampaignType.sms]: 'general.sms',
};

export enum CampaignStatus {
  none = 0,
  queued = 1,
  sending = 2,
  sent = 3,
  error = 4,
  draft = 5,
  scheduled = 6,
  automated = 7,
  building = 8,
}

export const CampaignStatusLabels: Record<CampaignStatus, string> = {
  [CampaignStatus.none]: 'general.none',
  [CampaignStatus.queued]: 'general.queued',
  [CampaignStatus.sending]: 'general.sending',
  [CampaignStatus.sent]: 'general.sent',
  [CampaignStatus.error]: 'general.error',
  [CampaignStatus.draft]: 'general.draft',
  [CampaignStatus.scheduled]: 'general.scheduled',
  [CampaignStatus.automated]: 'marketing.automated',
  [CampaignStatus.building]: 'general.sending',
};

export enum CampaignContentType {
  marketing = 0,
  administrative = 1,
}

export const CampaignContentTypeLabels: Record<CampaignContentType, string> = {
  [CampaignContentType.marketing]: 'general.marketing',
  [CampaignContentType.administrative]: 'marketing.administrative',
};

// Helper Classes

export class CampaignAttachment extends BaseModel {
  guid!: string;
  type!: string;

  get isImage(): boolean {
    return ['image/png', 'image/jpeg', 'image/gif'].includes(this.type);
  }
}

export type CampaignEmailStatus = boolean | number | string | null;

export class CampaignEmail extends BaseModel {
  idLocationEmailSend!: number;
  idLocationMassSend!: number;
  idCustomer?: number | null;
  customer?: { displayName?: string } | null;
  to!: string;
  subject?: string | null;
  body?: string | null;
  created!: number;
  success!: CampaignEmailStatus;
  messageID?: string | null;
  delivered!: CampaignEmailStatus;
  opened!: CampaignEmailStatus;
  clicked!: CampaignEmailStatus;
  bounced!: CampaignEmailStatus;
  complained!: CampaignEmailStatus;
  unsubscribed!: CampaignEmailStatus;
  provider?: string | null;

  override objectID(): string | null {
    const id = Number(this.idLocationEmailSend);

    return id > 0 ? id.toString() : null;
  }
}

export class CampaignStats extends BaseModel {
  recentOpens!: CampaignEmail[];

  static override relations(): RelationConfig {
    return {
      recentOpens: { type: CampaignEmail },
    };
  }
}

// Main Model

export class Campaign extends BaseModel {
  idLocationMassSend!: number;
  type!: CampaignType;
  status!: CampaignStatus;
  contentType!: CampaignContentType;
  attachments!: CampaignAttachment[];
  analyticsData!: { clicked: number; opened: number; total: number };

  constructor(data?: any) {
    super(data);

    this.type = this.normaliseEnumValue(this.type) as CampaignType;
    this.status = this.normaliseEnumValue(this.status) as CampaignStatus;
    this.contentType = this.normaliseEnumValue(this.contentType) as CampaignContentType;
  }

  static override relations(): RelationConfig {
    return {
      attachments: { type: CampaignAttachment },
    };
  }

  private normaliseEnumValue<T>(value: T): T | number {
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }

    return value;
  }

  override objectID(): string | null {
    const id = Number(this.idLocationMassSend);

    return id > 0 ? id.toString() : null;
  }

  get reachedLabel(): string {
    const data = this.analyticsData;
    return Math.round((Number(data?.clicked) / Number(data?.total) || 0) * 100) + '%';
  }

  get openedLabel(): string {
    const data = this.analyticsData;
    return Math.round((Number(data?.opened) / Number(data?.total) || 0) * 100) + '%';
  }

  get unopenedLabel(): string {
    const data = this.analyticsData;
    return Math.round(((Number(data?.total) - Number(data?.opened)) / Number(data?.total) || 0) * 100) + '%';
  }

  get clickedLabel(): string {
    const data = this.analyticsData;
    return Math.round((Number(data?.clicked) / Number(data?.total) || 0) * 100) + '%';
  }

  get isCampaignTypeEmail(): boolean {
    return this.type === CampaignType.email;
  }

  get isCampaignTypeSMS(): boolean {
    return this.type === CampaignType.sms;
  }

  get isCampaignStatusNone(): boolean {
    return this.status === CampaignStatus.none;
  }

  get isCampaignStatusQueued(): boolean {
    return this.status === CampaignStatus.queued;
  }

  get isCampaignStatusSending(): boolean {
    return this.status === CampaignStatus.sending || this.status === CampaignStatus.building;
  }

  get isCampaignStatusSent(): boolean {
    return this.status === CampaignStatus.sent;
  }

  get isCampaignStatusError(): boolean {
    return this.status === CampaignStatus.error;
  }

  get isCampaignStatusDraft(): boolean {
    return this.status === CampaignStatus.draft;
  }

  get isCampaignStatusScheduled(): boolean {
    return this.status === CampaignStatus.scheduled;
  }

  get isContentTypeMarketing(): boolean {
    return this.contentType === CampaignContentType.marketing;
  }

  get isContentTypeAdministrative(): boolean {
    return this.contentType === CampaignContentType.administrative;
  }
}
